/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useEffect, useMemo } from "react";
import { Transition } from "react-transition-group";
import { BEM } from "@adapptio/lib-components";
import { useForkRef } from "../../../hooks";
import { getAutoHeightDuration } from "../../../utils";
import { isString } from "@adapptio/lib-shared";

import "./Collapse.scss";

export interface ICollapseProps {
	children?: React.ReactNode;
	className?: string;
	open?: boolean;
	orientation?: "vertical" | "horizontal";
	collapsedSize?: number | string;
	unmountOnExit?: boolean;
}

export const Collapse = React.forwardRef<HTMLDivElement, ICollapseProps>(
	({ children, className, open, orientation, collapsedSize: collapsedSizeProp, unmountOnExit }, ref) => {
		const isHorizontal = orientation === "horizontal";
		const size = isHorizontal ? "width" : "height";
		const collapsedSize = isString(collapsedSizeProp) ? collapsedSizeProp : `${collapsedSizeProp}px`;

		const timer = React.useRef<ReturnType<typeof setTimeout>>();
		const autoTransitionDuration = React.useRef<number>();

		const nodeRef = React.useRef<HTMLDivElement>(null);
		const forkRef = useForkRef(ref, nodeRef);

		const wrapperRef = React.useRef<HTMLDivElement>(null);

		const style = useMemo(
			() => ({
				[isHorizontal ? "minWidth" : "minHeight"]: collapsedSize
			}),
			[ isHorizontal, collapsedSize ]
		);

		const getWrapperSize = () =>
			wrapperRef.current ? wrapperRef.current[isHorizontal ? "clientWidth" : "clientHeight"] : 0;

		const normalizedTransitionCallback =
			(callback: (node: HTMLDivElement, maybeIsAppearing?: boolean) => void) =>
			(maybeIsAppearing?: boolean) => {
				if (callback) {
					const node = nodeRef.current;

					// onEnterXxx and onExitXxx callbacks have a different arguments.length value.
					if (maybeIsAppearing === undefined) {
						callback(node);
					} else {
						callback(node, maybeIsAppearing);
					}
				}
			};

		const handleEnter = normalizedTransitionCallback((node) => {
			if (wrapperRef.current && isHorizontal) {
				// Set absolute position to get the size of collapsed content
				wrapperRef.current.style.position = "absolute";
			}
			node.style[size] = collapsedSize;
		});

		const handleEntering = normalizedTransitionCallback((node) => {
			const wrapperSize = getWrapperSize();

			if (wrapperRef.current && isHorizontal) {
				// After the size is read reset the position back to default
				wrapperRef.current.style.position = "";
			}

			const duration = getAutoHeightDuration(wrapperSize);
			node.style.transitionDuration = `${duration}ms`;
			autoTransitionDuration.current = duration;

			node.style[size] = `${wrapperSize}px`;
			node.style.transitionTimingFunction = "ease";
		});

		const handleEntered = normalizedTransitionCallback((node: HTMLDivElement) => {
			node.style[size] = "auto";
		});

		const handleExit = normalizedTransitionCallback((node) => {
			node.style[size] = `${getWrapperSize()}px`;
		});

		const handleExiting = normalizedTransitionCallback((node) => {
			const wrapperSize = getWrapperSize();

			const duration = getAutoHeightDuration(wrapperSize);
			node.style.transitionDuration = `${duration}ms`;
			autoTransitionDuration.current = duration;

			node.style[size] = collapsedSize;
			node.style.transitionTimingFunction = "ease";
		});

		const handleAddEndListener = (next) => {
			timer.current = setTimeout(next, autoTransitionDuration.current || 0);
		};

		useEffect(() => {
			return () => {
				clearTimeout(timer.current);
			};
		}, []);

		return (
			<Transition
				in={open}
				onEnter={handleEnter}
				onEntered={handleEntered}
				onEntering={handleEntering}
				onExit={handleExit}
				onExiting={handleExiting}
				addEndListener={handleAddEndListener}
				nodeRef={nodeRef}
				timeout={null}
				unmountOnExit={unmountOnExit}
			>
				{(state, childProps) => {
					const baseClass = BEM("edt-collapse", null, {
						orientation,
						state,
						hidden: state === "exited" && !open && collapsedSize === "0px"
					});

					if (className) {
						baseClass.add(className);
					}

					return (
						<div className={baseClass.toClassName()} ref={forkRef} style={style} {...childProps}>
							<div className={baseClass.element("wrapper").toClassName()} ref={wrapperRef}>
								<div className={baseClass.element("inner").toClassName()}>{children}</div>
							</div>
						</div>
					);
				}}
			</Transition>
		);
	}
);

Collapse.defaultProps = {
	orientation: "vertical",
	collapsedSize: "0px"
};
